import { computed, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import type { PaginatorState } from 'primeng/paginator';

/** Which page is on screen and how many rows a page holds, as app-paginator reads and changes them. */
export interface PageState {
  readonly page: WritableSignal<number>;
  readonly size: WritableSignal<number>;
  readonly first: Signal<number>;
  onPageChange(event: PaginatorState): void;
}

/** The page state a list hands to app-paginator, whether the rows come a page at a time or all at once. */
export interface PagedList<T> extends PageState {
  readonly items: Signal<T[]>;
  readonly totalRecords: Signal<number>;
}

export interface PagedListOptions {
  /** Rows per page until the user picks another size. Defaults to 10. */
  readonly size?: number;
}

/**
 * The page state pagedList and pagedResource share. The page goes back to the first one whenever `resetOn`
 * changes, since another list may not have the page the old one was on.
 */
export function pageState(resetOn: Signal<unknown>, initialSize: number): PageState {
  const page = linkedSignal({ source: resetOn, computation: () => 0 });
  const size = signal(initialSize);

  return {
    page,
    size,
    first: computed(() => page() * size()),
    onPageChange(event: PaginatorState) {
      page.set(event.page ?? 0);
      size.set(event.rows ?? initialSize);
    }
  };
}

/**
 * pagedResource for rows the screen already holds, such as an endpoint that answers with the whole list: the
 * same page state and paginator wiring, with the page cut here. Every signal `rows` reads is tracked; other rows
 * (reloaded, sorted, filtered) are another list, so it goes back to the first page.
 */
export function pagedList<T>(rows: () => readonly T[], options: PagedListOptions = {}): PagedList<T> {
  const all = computed(rows);
  const state = pageState(all, options.size ?? 10);
  const { first, size } = state;

  return {
    ...state,
    items: computed(() => all().slice(first(), first() + size())),
    totalRecords: computed(() => all().length)
  };
}
